/**
 * AUDIO — ambient bed: filtered-noise rain with slow modulation, vinyl
 * crackle, and an office fan hum that fades in indoors. Owns the
 * indoor/outdoor crossfade. Owned by AUDIO; see CONTRACTS.md.
 */
import type { ZoneId } from '../core/types'
import {
  BED_FADE_IN_S,
  CRACKLE_GAIN,
  CRACKLE_HIGHPASS_HZ,
  CRACKLE_IMPULSE_S,
  CRACKLE_LOOP_SECONDS,
  CRACKLE_MAX_GAP_S,
  CRACKLE_MIN_GAP_S,
  FAN_HUM_FREQ_HIGH_HZ,
  FAN_HUM_FREQ_LOW_HZ,
  FAN_HUM_GAIN,
  RAIN_FILTER_HZ_INDOOR,
  RAIN_FILTER_HZ_OUTDOOR,
  RAIN_FILTER_Q,
  RAIN_GAIN_INDOOR,
  RAIN_GAIN_OUTDOOR,
  RAIN_LFO_DEPTH_HZ,
  RAIN_LFO_HZ,
  ZONE_FADE_S
} from './constants'
import { rampTo, startLoop } from './engine'
import type { AudioEngine } from './engine'

export interface AmbientBed {
  setZone(zone: ZoneId): void
}

/** Sparse impulse train that loops as vinyl surface crackle. */
const createCrackleBuffer = (ctx: AudioContext): AudioBuffer => {
  const sampleRate = ctx.sampleRate
  const length = Math.floor(sampleRate * CRACKLE_LOOP_SECONDS)
  const buffer = ctx.createBuffer(1, length, sampleRate)
  const data = buffer.getChannelData(0)
  const impulseSamples = Math.floor(sampleRate * CRACKLE_IMPULSE_S)

  let cursor = 0
  while (cursor < length) {
    const gap = CRACKLE_MIN_GAP_S + Math.random() * (CRACKLE_MAX_GAP_S - CRACKLE_MIN_GAP_S)
    cursor += Math.floor(sampleRate * gap)
    if (cursor >= length) break
    const amplitude = 0.4 + Math.random() * 0.6
    for (let i = 0; i < impulseSamples && cursor + i < length; i += 1) {
      data[cursor + i] = amplitude * (1 - i / impulseSamples) * (Math.random() * 2 - 1)
    }
    cursor += impulseSamples
  }
  return buffer
}

const startRainNoise = (engine: AudioEngine): { gain: GainNode; filter: BiquadFilterNode } => {
  const filter = engine.ctx.createBiquadFilter()
  filter.type = 'lowpass'
  filter.frequency.value = RAIN_FILTER_HZ_OUTDOOR
  filter.Q.value = RAIN_FILTER_Q

  // Slow LFO breathes the cutoff so the rain doesn't read as static hiss.
  const lfo = engine.ctx.createOscillator()
  lfo.frequency.value = RAIN_LFO_HZ
  const lfoDepth = engine.ctx.createGain()
  lfoDepth.gain.value = RAIN_LFO_DEPTH_HZ
  lfo.connect(lfoDepth)
  lfoDepth.connect(filter.frequency)
  lfo.start()

  const gain = startLoop(engine, engine.noiseBuffer, [filter], 0)
  rampTo(engine, gain.gain, RAIN_GAIN_OUTDOOR, BED_FADE_IN_S)
  return { gain, filter }
}

const startCrackle = (engine: AudioEngine): void => {
  const highpass = engine.ctx.createBiquadFilter()
  highpass.type = 'highpass'
  highpass.frequency.value = CRACKLE_HIGHPASS_HZ

  const gain = startLoop(engine, createCrackleBuffer(engine.ctx), [highpass], 0)
  rampTo(engine, gain.gain, CRACKLE_GAIN, BED_FADE_IN_S)
}

const startFanHum = (engine: AudioEngine): GainNode => {
  const gain = engine.ctx.createGain()
  gain.gain.value = 0
  gain.connect(engine.master)

  const frequencies: readonly number[] = [FAN_HUM_FREQ_LOW_HZ, FAN_HUM_FREQ_HIGH_HZ]
  frequencies.forEach((freq) => {
    const osc = engine.ctx.createOscillator()
    osc.type = 'sine'
    osc.frequency.value = freq
    osc.connect(gain)
    osc.start()
  })
  return gain
}

/**
 * Start the full ambient bed. Rain is bright and loud outdoors, muffled and
 * quiet indoors; the fan hum exists only indoors.
 */
export const startAmbientBed = (engine: AudioEngine): AmbientBed => {
  const rain = startRainNoise(engine)
  startCrackle(engine)
  const fanGain = startFanHum(engine)

  const setZone = (zone: ZoneId): void => {
    const indoor = zone === 'indoor'
    rampTo(engine, rain.gain.gain, indoor ? RAIN_GAIN_INDOOR : RAIN_GAIN_OUTDOOR, ZONE_FADE_S)
    rampTo(
      engine,
      rain.filter.frequency,
      indoor ? RAIN_FILTER_HZ_INDOOR : RAIN_FILTER_HZ_OUTDOOR,
      ZONE_FADE_S
    )
    rampTo(engine, fanGain.gain, indoor ? FAN_HUM_GAIN : 0, ZONE_FADE_S)
  }

  return { setZone }
}
